import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AxisModel, CategoryService, ChartModule, ChartTheme, ColumnSeriesService, DataLabelService, LegendSettingsModel, SeriesModel, TooltipService, TooltipSettingsModel } from '@syncfusion/ej2-angular-charts';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { StatisticStatus, TripClosedReason, TripDetailDTO, TripStatus, UnavailableReason } from 'src/app/base/models/models.swagger.generated';
import { ThemeMode } from 'src/app/base/services/theme.model';
import { ThemeService } from 'src/app/base/services/theme.service';

// Illustrative reference max for the speed gauges — a common speedometer top-end, not a hard data limit.
const REFERENCE_MAX_SPEED_KMH = 160;

type StatLike<T> = { value?: T | undefined; status: StatisticStatus; reasons?: UnavailableReason[] | undefined };

@Component({
  selector: 'app-trip-detail-dialog',
  templateUrl: './trip-detail-dialog.component.html',
  styleUrls: ['./trip-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DialogModule, TranslatePipe, ChartModule],
  providers: [ColumnSeriesService, CategoryService, TooltipService, DataLabelService],
})
export class TripDetailDialogComponent implements OnChanges, OnDestroy {

  @Input() tripDetail: TripDetailDTO | null = null;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  public chartTheme: ChartTheme = 'Bootstrap5';

  public readonly engineLoadHistogramPrimaryXAxis: AxisModel = {
    valueType: 'Category',
    majorGridLines: { width: 0 },
    labelIntersectAction: 'Rotate45',
  };

  public readonly engineLoadHistogramPrimaryYAxis: AxisModel = {
    title: 'min',
    minimum: 0,
    majorTickLines: { width: 0 },
  };

  public readonly engineLoadHistogramTooltip: TooltipSettingsModel = {
    enable: true,
    format: '${point.x}: ${point.y} min',
  };

  public readonly engineLoadHistogramLegend: LegendSettingsModel = { visible: false };

  // Computed once per tripDetail change (see ngOnChanges), never via a getter: this component
  // is checked on every change detection cycle (including the mousemove events a chart hover
  // fires), and a getter would hand the ejs-chart a new array/object reference on every one of
  // those checks — which Syncfusion treats as "the data changed" and redraws for, killing the
  // tooltip mid-hover.
  public engineLoadHistogramSeries: SeriesModel[] = [];

  private readonly themeSubscription: Subscription;

  constructor(private translateService: TranslateService, themeService: ThemeService) {
    this.chartTheme = this.chartThemeFor(themeService.getCurrentMode());
    this.themeSubscription = themeService.mode$.subscribe(mode => this.chartTheme = this.chartThemeFor(mode));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['tripDetail']) {
      this.engineLoadHistogramSeries = this.buildEngineLoadHistogramSeries();
    }
  }

  public ngOnDestroy(): void {
    this.themeSubscription.unsubscribe();
  }

  public close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // One bucket's range becomes the chart's category label (e.g. "20-30%"); durationMinutes is
  // rounded to keep the tooltip/data label readable.
  private buildEngineLoadHistogramSeries(): SeriesModel[] {
    const buckets = this.statValue(this.tripDetail?.engineLoadHistogram);
    const data = (buckets ?? []).map(bucket => ({
      range: `${bucket.lowerBound}-${bucket.upperBound}%`,
      durationMinutes: Math.round(bucket.durationMinutes * 10) / 10,
    }));

    return [{
      dataSource: data,
      xName: 'range',
      yName: 'durationMinutes',
      type: 'Column',
      name: 'Duration',
      fill: 'var(--color-primary-500)',
    }];
  }

  private chartThemeFor(mode: ThemeMode): ChartTheme {
    return mode === 'dark' ? 'Bootstrap5Dark' : 'Bootstrap5';
  }

  // Every trip metric on TripDetailDTO (except the raw TripDTO fields) is wrapped as
  // { value, status: Calculated|Unavailable, reasons }, since not every module has computed
  // it yet. These three helpers unwrap that shape consistently across the template.
  public isUnavailable(stat: StatLike<any> | undefined): boolean {
    return !stat || stat.status === StatisticStatus.Unavailable;
  }

  public statValue<T>(stat: StatLike<T> | undefined): T | undefined {
    return this.isUnavailable(stat) ? undefined : stat?.value;
  }

  public unavailableTooltip(stat: StatLike<any> | undefined): string {
    if (!stat?.reasons?.length) {
      return this.translateService.instant('i18n.domain.analytics.trip-overview.detail.unavailable');
    }
    return stat.reasons.map(r => r.detail || r.reason).join('; ');
  }

  public formatDateTime(dt: DateTime | undefined): string {
    return dt ? dt.toFormat('dd.MM.yyyy HH:mm') : '—';
  }

  public formatTripStatus(status: TripStatus | undefined): string {
    if (status == null) {
      return '—';
    }
    return this.translateService.instant('i18n.shared.enums.TripStatus.' + TripStatus[status]);
  }

  public formatTripClosedReason(reason: TripClosedReason | undefined): string {
    if (reason == null) {
      return '—';
    }
    return this.translateService.instant('i18n.shared.enums.TripClosedReason.' + TripClosedReason[reason]);
  }

  public statusBadgeClass(status: TripStatus | undefined): string {
    switch (status) {
      case TripStatus.Open: return 'text-bg-primary';
      case TripStatus.PendingEndOfTrip: return 'text-bg-warning';
      case TripStatus.Closed: return 'text-bg-secondary';
      default: return 'text-bg-secondary';
    }
  }

  public formatNumber(value: number | null | undefined, unit: string = '', digits: number = 1): string {
    if (value == null) {
      return '—';
    }
    return unit ? `${value.toFixed(digits)} ${unit}` : value.toFixed(digits);
  }

  public formatCount(value: number | null | undefined): string {
    return value == null ? '—' : value.toString();
  }

  public formatBoolean(value: boolean | null | undefined): string {
    if (value == null) {
      return '—';
    }
    return this.translateService.instant(value ? 'i18n.shared.yes' : 'i18n.shared.no');
  }

  public boolBadgeClass(value: boolean | null | undefined, warnWhenTrue: boolean): string {
    if (value == null) {
      return 'text-bg-secondary';
    }
    if (warnWhenTrue) {
      return value ? 'text-bg-warning' : 'text-bg-success';
    }
    return value ? 'text-bg-success' : 'text-bg-secondary';
  }

  public speedPercent(value: number | null | undefined): number {
    if (value == null) {
      return 0;
    }
    return Math.min(100, Math.round((value / REFERENCE_MAX_SPEED_KMH) * 100));
  }

  public driverScorePercent(score: number | null | undefined): number {
    return score == null ? 0 : Math.min(100, Math.max(0, Math.round(score)));
  }

  public driverScoreBarClass(score: number | null | undefined): string {
    if (score == null) {
      return 'bg-secondary';
    }
    if (score >= 80) {
      return 'bg-success';
    }
    if (score >= 50) {
      return 'bg-warning';
    }
    return 'bg-danger';
  }

  public eventCountBadgeClass(value: number | null | undefined): string {
    if (value == null) {
      return 'text-bg-secondary';
    }
    return value > 0 ? 'text-bg-warning' : 'text-bg-success';
  }
}
